import { createFileRoute } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { QuizContent } from '@/courses/quiz/QuizContent';
import { PreviewLayout } from '@/preview/PreviewLayout';

export const Route = createFileRoute('/preview/quiz/$lessonId')({
  component: PreviewQuiz,
});

function PreviewQuiz() {
  const lessonId = Number(Route.useParams().lessonId);
  const questions = api.previewLessonQuestions.useQuery(
    { path: { lesson_id: lessonId } },
    { retry: false },
  );
  // Marks the attempt the way the grader would, and keeps nothing.
  const mark = api.previewMark.useMutation();

  return (
    <PreviewLayout current={{ quizLessonId: lessonId }}>
      {({ course, lessons }) => {
        const lesson = lessons.find((l) => l.id === lessonId);
        if (!questions.data || !lesson) return null;
        const wrong =
          mark.data?.questions.filter((q) => !q.correct).length ?? 0;
        const panel = mark.isError ? (
          <AlertBox variant="error">
            {/* Translation Needed */}
            Could not check these answers. The preview may have ended; open a
            new one from the admin.
          </AlertBox>
        ) : (
          mark.data && (
            <Marked $passed={mark.data.passed} role="status">
              {/* Translation Needed */}
              Scored {mark.data.score} out of 100:{' '}
              {mark.data.passed ? 'this would pass' : 'this would not pass'}.{' '}
              {wrong === 0
                ? 'Every question is marked right.'
                : `${wrong} ${wrong === 1 ? 'question is' : 'questions are'} marked wrong below, each with its answer key.`}{' '}
              Nothing was saved.
            </Marked>
          )
        );
        const marks = Object.fromEntries(
          (mark.data?.questions ?? []).map((q) => [
            q.question_id,
            { correct: q.correct, expected: q.expected },
          ]),
        );
        return (
          <QuizContent
            courseTitle={course.title}
            courseSlug={course.slug}
            lessonTitle={lesson.title}
            lessonSlug={lesson.slug}
            // Titled by its lesson, as the student quiz page is.
            quizTitle={lesson.title}
            questions={questions.data}
            isStudent={false}
            canSubmit
            // Translation Needed
            submitLabel="Check answers"
            // Translation Needed
            instructions={`There are ${questions.data.length} questions in this quiz. Check answers marks them the way a student's answers would be marked. Nothing is saved.`}
            panel={panel}
            marks={marks}
            breadcrumbs={[
              { label: course.title, to: '/preview/' },
              {
                label: lesson.title,
                to: '/preview/quiz/$lessonId/',
                params: { lessonId: String(lessonId) },
              },
            ]}
            // Failure shows in the panel above; nothing to throw to.
            onSubmitQuiz={async (answers) => {
              await mark
                .mutateAsync({
                  body: { lesson_id: lessonId, topic_id: null, answers },
                })
                .catch(() => undefined);
            }}
          />
        );
      }}
    </PreviewLayout>
  );
}

const Marked = styled.p<{ $passed: boolean }>`
  background: color-mix(
    in srgb,
    ${(p) => (p.$passed ? 'var(--success)' : 'var(--error)')} 12%,
    var(--white)
  );
  border-left: 3px solid
    ${(p) => (p.$passed ? 'var(--success)' : 'var(--error)')};
  color: var(--grey-900);
  margin: 16px 0;
  padding: 10px 14px;
`;
